import { Sparkle } from 'lucide-react';
import { Link } from 'react-router-dom';

import type { OrganisationPlan } from 'app/hooks/useOrganisationFeatures';
import { RightPanelOverlayModal } from 'app/shared-components/modals';
import type { NewPlansSegment } from 'app/utils/newPlansSegment';

import INCLUDED_FEATURES from './includedFeatures';

const TITLE_ID = 'new-plan-features-title';

interface NewPlanFeaturesModalProps {
  segment: NewPlansSegment;
  plan: OrganisationPlan;
  /** Closing it in any way counts as seen. */
  onClose: () => void;
}

/**
 * "Good news" pop-up listing what the new plans added to the customer's plan. When to show it is
 * `useNewPlanFeaturesPopUp`'s call.
 */
export default function NewPlanFeaturesModal({
  segment,
  plan,
  onClose,
}: NewPlanFeaturesModalProps): JSX.Element {
  const planName = plan === 'plus' ? 'Plus plan' : 'plan';

  return (
    <RightPanelOverlayModal open onClose={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={TITLE_ID}
        className="vtwo:flex vtwo:w-[570px] vtwo:max-w-[calc(100%-32px)] vtwo:flex-col vtwo:overflow-hidden vtwo:rounded-[16px] vtwo:bg-white vtwo:shadow-[0_24px_64px_rgba(0,0,0,0.25)] vtwo:outline-none"
      >
        <div className="vtwo:flex vtwo:flex-col vtwo:gap-2.5 vtwo:bg-[linear-gradient(102.82deg,#4db181_5.6%,#3f7284_85.37%)] vtwo:px-7 vtwo:pt-7 vtwo:pb-5.5 vtwo:text-white">
          <span className="vtwo:flex vtwo:size-11 vtwo:items-center vtwo:justify-center vtwo:rounded-[12px] vtwo:bg-white/20">
            <Sparkle className="vtwo:size-5.5" strokeWidth={1.8} aria-hidden />
          </span>
          <h2
            id={TITLE_ID}
            className="vtwo:m-0 vtwo:text-[22px] vtwo:font-semibold vtwo:tracking-[-0.01em]"
          >
            Good news: your {planName} just got better
          </h2>
          <p className="vtwo:m-0 vtwo:text-[15px] vtwo:leading-[1.45] vtwo:text-white/90">
            These features are now included in your plan, at no extra cost.
          </p>
        </div>

        <ul className="vtwo:m-0 vtwo:flex vtwo:list-none vtwo:flex-col vtwo:gap-1 vtwo:px-4.5 vtwo:py-3.5">
          {INCLUDED_FEATURES[segment].map(({ icon: Icon, name, benefit }) => (
            <li
              key={name}
              className="vtwo:flex vtwo:items-center vtwo:gap-3.5 vtwo:px-2.5 vtwo:py-3"
            >
              <span className="vtwo:flex vtwo:size-10 vtwo:shrink-0 vtwo:items-center vtwo:justify-center vtwo:rounded-[10px] vtwo:bg-[#ecf7f1] vtwo:text-healthera">
                <Icon className="vtwo:size-5" strokeWidth={1.8} aria-hidden />
              </span>
              <span className="vtwo:flex vtwo:flex-col vtwo:gap-0.5">
                <span className="vtwo:text-[15px] vtwo:font-semibold vtwo:text-typography-primary">
                  {name}
                </span>
                <span className="vtwo:text-[13.5px] vtwo:leading-[1.4] vtwo:text-[#696d7f]">
                  {benefit}
                </span>
              </span>
            </li>
          ))}
        </ul>

        <div className="vtwo:flex vtwo:items-center vtwo:gap-3 vtwo:border-t vtwo:border-[#eef0f3] vtwo:px-7 vtwo:pt-4 vtwo:pb-6">
          <Link
            to="/announcements"
            onClick={onClose}
            className="vtwo:text-sm vtwo:text-[#696d7f] vtwo:no-underline vtwo:hover:text-typography-primary"
          >
            See all in What’s New
          </Link>
          <button
            type="button"
            onClick={onClose}
            className="vtwo:ml-auto vtwo:h-10.5 vtwo:cursor-pointer vtwo:rounded-[8px] vtwo:border-0 vtwo:bg-[#134e4a] vtwo:px-5.5 vtwo:text-[15px] vtwo:font-semibold vtwo:text-white vtwo:hover:bg-[#134e4a]/90 vtwo:focus-visible:outline-2 vtwo:focus-visible:outline-offset-2 vtwo:focus-visible:outline-[#134e4a]"
          >
            Got it
          </button>
        </div>
      </div>
    </RightPanelOverlayModal>
  );
}
